/**
 * Floating dropdown for the chat composer's `@` file-mention picker.
 * Shown when the user types `@` and an attached folder exists.
 *
 * Filters the cached file index against the user's typed query,
 * supports keyboard nav (↑↓ + Enter + Esc), and reports the chosen
 * file back to the parent so it can insert a token in the editor.
 *
 * @module file-agent/components/file-mention-dropdown
 */

import { FileText, FolderSearch } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { useAnchoredMenuPosition } from "@/hooks/use-anchored-menu-position";
import { useScrollActiveIntoView } from "@/hooks/use-scroll-active-into-view";
import { filterFiles, type IndexedFile, type FileIndex } from "../lib/file-index";

interface FileMentionDropdownProps {
  open: boolean;
  /** Substring after `@` — drives filtering. */
  searchQuery: string;
  /** Editor DOM element used for positioning. */
  anchorElement: HTMLElement | null;
  /** Pre-walked file index from `useFileIndex`. */
  index: FileIndex | null;
  loading: boolean;
  onSelect: (file: IndexedFile) => void;
  onOpenChange: (open: boolean) => void;
}

/**
 * Anchored portal-rendered popover that lists files from the attached
 * folder. Falls back to a friendly message while the index is loading or
 * when the index is empty (no folder, gitignored everything, etc.).
 */
export function FileMentionDropdown({
  open,
  searchQuery,
  anchorElement,
  index,
  loading,
  onSelect,
  onOpenChange,
}: FileMentionDropdownProps) {
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLDivElement>(null);

  // Collision-aware floating position. Prefers above (matching the prior
  // behaviour) but flips/clamps so it never goes off-screen, and matches the
  // composer width like a select dropdown.
  const position = useAnchoredMenuPosition({
    anchorElement,
    menuRef: containerRef,
    open,
    preferredSide: "up",
    matchAnchorWidth: true,
    minWidth: 320,
  });

  useEffect(() => {
    setMounted(true);
  }, []);

  const filtered = useMemo(() => {
    if (!index) return [];
    return filterFiles(index, searchQuery, 20);
  }, [index, searchQuery]);

  // Reset highlighted item whenever the result set shifts.
  useEffect(() => {
    setSelectedIndex(0);
  }, [searchQuery, filtered.length]);

  // Keyboard nav. Use capture so the editor's handlers don't see Enter etc.
  useEffect(() => {
    if (!open) return;
    const handler = (event: KeyboardEvent) => {
      if (event.key === "ArrowDown") {
        event.preventDefault();
        setSelectedIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
      } else if (event.key === "ArrowUp") {
        event.preventDefault();
        setSelectedIndex((i) => Math.max(i - 1, 0));
      } else if (event.key === "Enter") {
        if (filtered[selectedIndex]) {
          event.preventDefault();
          onSelect(filtered[selectedIndex]);
        }
      } else if (event.key === "Escape") {
        event.preventDefault();
        onOpenChange(false);
      }
    };
    window.addEventListener("keydown", handler, true);
    return () => window.removeEventListener("keydown", handler, true);
  }, [open, filtered, selectedIndex, onSelect, onOpenChange]);

  // Keep the keyboard-focused row visible as the user arrows through the list.
  useScrollActiveIntoView(listRef, selectedIndex, open);

  if (!open || !mounted) return null;

  const showEmpty = !loading && filtered.length === 0;
  const truncated = index?.truncated ?? false;

  // Path of the keyboard-selected row, used to drive both the highlight
  // (Command `value`) and the scroll marker (`data-active`) below.
  const activePath = filtered[selectedIndex]?.path ?? "";

  return createPortal(
    <div
      ref={containerRef}
      className="fixed z-50 rounded-lg border border-border bg-popover shadow-lg"
      style={{
        top: position.top,
        left: position.left,
        width: position.width,
        maxHeight: 280,
      }}
    >
      <Command
        shouldFilter={false}
        // Keep cmdk's own selection in lockstep with our manual selectedIndex,
        // otherwise cmdk's stuck first-row highlight competes with ours.
        value={activePath}
        onValueChange={(value) => {
          const nextIndex = filtered.findIndex((file) => file.path === value);
          if (nextIndex >= 0) {
            setSelectedIndex(nextIndex);
          }
        }}
      >
        <CommandList className="max-h-[260px] overflow-y-auto" ref={listRef}>
          {loading && (
            <div className="flex items-center gap-2 px-3 py-2 text-xs text-muted-foreground">
              <FolderSearch className="h-3.5 w-3.5" />
              Indexing files…
            </div>
          )}
          {showEmpty && (
            <CommandEmpty className="px-3 py-3 text-center text-xs text-muted-foreground">
              No files match "{searchQuery}"
            </CommandEmpty>
          )}
          {filtered.length > 0 && (
            <CommandGroup heading={searchQuery ? `Matches for "${searchQuery}"` : "Files"}>
              {filtered.map((file) => (
                <CommandItem
                  key={file.path}
                  value={file.path}
                  data-active={file.path === activePath}
                  onSelect={() => onSelect(file)}
                  className="flex cursor-pointer items-center gap-2 px-2 py-1.5 text-xs"
                >
                  <FileText className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
                  <div className="flex min-w-0 flex-col">
                    <span className="truncate font-medium">{file.name}</span>
                    {file.path !== file.name && (
                      <span className="truncate text-[10.5px] text-muted-foreground">
                        {file.path}
                      </span>
                    )}
                  </div>
                </CommandItem>
              ))}
            </CommandGroup>
          )}
          {truncated && (
            <div className="border-t border-border/60 px-3 py-1.5 text-[10.5px] text-muted-foreground">
              Showing first matches — refine your search to find more files.
            </div>
          )}
        </CommandList>
      </Command>
    </div>,
    document.body,
  );
}
